import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import { Sync } from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto"
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const statusColor = status => {
  if (status === "APPROVED") return "primary";
  if (status === "REJECTED") return "secondary";
  return "default";
};

const MetaTemplates = () => {
  const classes = useStyles();

  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/meta-cloud");
        setConnections(data);
        if (data.length > 0) setSelectedConnection(data[0].id);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedConnection) return;
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnection]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/meta-cloud/${selectedConnection}/templates`);
      setTemplates(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/meta-cloud/${selectedConnection}/sync-templates`);
      toast.success(
        `Sincronizado! ${data.created} novos, ${data.updated} atualizados (total ${data.total}).`
      );
      fetchTemplates();
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Templates Meta (WhatsApp Oficial)</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar}>
          <Select
            value={selectedConnection}
            onChange={e => setSelectedConnection(e.target.value)}
            variant="outlined"
            displayEmpty
          >
            <MenuItem value="" disabled>
              Selecione a conexão Meta
            </MenuItem>
            {connections.map(conn => (
              <MenuItem key={conn.id} value={conn.id}>
                {conn.name}
              </MenuItem>
            ))}
          </Select>

          <Button
            variant="contained"
            color="primary"
            startIcon={<Sync />}
            disabled={!selectedConnection || syncing}
            onClick={handleSync}
          >
            {syncing ? "Sincronizando..." : "Sincronizar templates da Meta"}
          </Button>
        </div>

        {loading ? (
          <CircularProgress />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Idioma</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Corpo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map(tpl => {
                const bodyComponent = (tpl.components || []).find(
                  c => c.type === "BODY"
                );
                return (
                  <TableRow key={tpl.id}>
                    <TableCell>{tpl.name}</TableCell>
                    <TableCell>{tpl.language}</TableCell>
                    <TableCell>{tpl.category}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={tpl.status}
                        color={statusColor(tpl.status)}
                      />
                    </TableCell>
                    <TableCell style={{ maxWidth: 400 }}>
                      {bodyComponent?.text || "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
              {templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhum template encontrado. Clique em "Sincronizar" para
                    buscar os templates aprovados na Meta.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default MetaTemplates;
